import { useMemo } from 'react';

import type { Contact, EnrichedContactWithCrm, RevealBulkResponse } from 'domain/contact';
import type { Crm, CrmAccount } from 'domain/crm';

import { useContactsPreviewData } from './useContactsPreviewData';
import { useRevealContactsFlow } from './useRevealContactsFlow';

export type UseContactsBulkPreviewProps = {
  crm: Crm;
  isEnabled: boolean;
  enrichedContactsWithCRM: EnrichedContactWithCrm[];
  setEnrichedContactsWithCRM: (contacts: EnrichedContactWithCrm[]) => void;
  onConfirm: (props: { updatedContacts?: EnrichedContactWithCrm[]; revealResult?: RevealBulkResponse }) => void;
  onExportAsLeadAfterReveal: (revealResponse?: RevealBulkResponse, updatedContacts?: Contact[]) => void;
  isExportAsLeadAfterReveal: boolean;
};

export const useContactsBulkExport = ({
  crm,
  isEnabled,
  enrichedContactsWithCRM,
  setEnrichedContactsWithCRM,
  onConfirm,
  onExportAsLeadAfterReveal,
  isExportAsLeadAfterReveal,
}: UseContactsBulkPreviewProps) => {
  const { onContinueToReveal, isPending, revealResult, resetRevealResult } = useRevealContactsFlow({
    isExportAsLeadAfterReveal,
    onExportAsLeadAfterReveal,
  });

  const { data: contactsPreview, isLoading: isPreviewLoading } = useContactsPreviewData({
    crm,
    isEnabled,
    enrichedContactsWithCRM,
    setEnrichedContactsWithCRM,
    onConfirm,
  });

  const unmatchedContacts = useMemo(
    () =>
      isPreviewLoading
        ? []
        : enrichedContactsWithCRM.filter((c) => c.preview?.matchedCompanies?.length !== 1 || c.preview?.isExistAsLead),
    [enrichedContactsWithCRM, isPreviewLoading]
  );

  const newlyUnmatchedContacts = useMemo(
    () => unmatchedContacts.filter((c) => !c.crmAccount && !c.preview?.isExistAsLead),
    [unmatchedContacts]
  );

  const contactsToExportCount = useMemo(
    () => enrichedContactsWithCRM.filter((c) => c.crmAccount).length,
    [enrichedContactsWithCRM]
  );

  const onContactChange = (contact: EnrichedContactWithCrm, crmAccount: CrmAccount) => {
    const sameCompanyContacts = newlyUnmatchedContacts.filter(
      (c) => c.preview?.accountName === contact.preview?.accountName
    );
    const updated = enrichedContactsWithCRM.map((c) =>
      c.id === contact.id || sameCompanyContacts.some((sc) => sc.id === c.id) ? { ...c, crmAccount } : c
    );
    setEnrichedContactsWithCRM(updated);
  };

  return {
    isLoading: isPreviewLoading || isPending,
    unmatchedContacts,
    isSubmitDisabled: newlyUnmatchedContacts.length > 0,
    matchedCount: (contactsPreview?.length || 0) - unmatchedContacts.length,
    total: contactsPreview?.length || 0,
    contactsToExportCount,
    onContactChange,
    onContinueToReveal,
    revealResult,
    resetRevealResult,
  };
};
